import { cloneDeep } from "lodash";
import moment from "moment";

import { REPORT_SENSOR_LIST, REPORT_SENSOR_REGISTRY, ROOM_TAG_PREFIX, ROOM_VIRTUAL_LDSU_OBJ } from "constant/report";
import { TagData } from "generated/models";
import reportService from "service/reportService";
import { ConsumptionChartBy, Device, GatewayDeviceList } from "types";
import {
    MonthTimeRange,
    ReportChartSeries,
    ReportIndividualData,
    ReportItemsState,
    ReportItemStateData,
    ReportItemType,
    ReportOverallData,
    ReportOverviewData,
    ReportRoom,
    ReportSensorType,
    ReportSetItemLoadingFunction,
    ReportSummaryInfo,
    ReportSummaryInfoOfOneItem,
} from "types/Reports";
import { SensorType } from "constant/gateway";
import { getPSLUsageMetricData, timeUnitMapping } from "service/proqueryService";
import { getSensorType } from "utils/sensorFunctions";
import {
    transformConsumptionDataToIndividualData,
    transformConsumptionDataToOverallData,
    transformRawRoomToReportRoom,
} from "./reportTransform";
import {
    createItemSeriesFromRawData,
    summaryReportChartSeries,
    getAverageFromSeriesAndTotal,
    computeReportChartSeries,
    isReportChartSeriesValid,
    createItemSeriesFromRoomUsageData,
} from "./report";
import { getCategoryOfMonth } from "./reportGenerate";
import {
    initialSummaryInfoSingleType,
    initialReportOverallData,
} from "./reportInitData";
import { convertSelectedIdsToRooms, convertSelectedIdsToDevices } from "./reportConvert";
import { SensorDetails } from "types/Sensors";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";

export const retrieveCurAndPrevRawDataOfRoom = async (
    room: ReportRoom,
    endDayOfMonth: moment.Moment,
) => {
    let rawDeviceData;
    let prevRawDeviceData;

    if (room.statusDevice) {
        const combinedResult = await Promise.all([
            reportService.getSensorData(
                room.statusDevice.gatewayId,
                room.statusDevice.ldsuUID,
                room.statusDevice.said,
                endDayOfMonth.unix(),
                'room',
            ),
            reportService.getSensorData(
                room.statusDevice.gatewayId,
                room.statusDevice.ldsuUID,
                room.statusDevice.said,
                moment(endDayOfMonth).subtract(1, 'month').endOf('month').unix(),
                'room',
            )
        ]);

        rawDeviceData = combinedResult[0];
        prevRawDeviceData = combinedResult[1];
    }

    return { rawDeviceData, prevRawDeviceData };
};

export const retrieveUsageDataOfRooms = async (
    rooms: ReportRoom[],
    endDayOfMonth: moment.Moment,
) => {
    const devices: SensorDetails[] = rooms.map((room) => {
        return {
            device_id: room.statusDevice?.ldsuUID ?? undefined,
            said: room.statusDevice?.said ?? 0,
            gateway_id: room.statusDevice?.gatewayId ?? undefined,
            name: room.roomName,
            color: 'not used',
        };
    }).filter((d) => d.device_id !== undefined);

    const queryTime = endDayOfMonth.unix();
    const groupBy = ConsumptionChartBy.Month;
    const { metricData, totalOnOff } = await getPSLUsageMetricData({ devices, queryTime, groupBy });

    return { metricData, totalOnOff };
};

export const retrieveReportRawRooms = async (currentOrgId: string) => {
    const rooms: TagData[] = [];

    const roomsGroupsZones = await reportService.getRoomsGroupsZones(currentOrgId, undefined, true);

    if (roomsGroupsZones.error || !roomsGroupsZones.data || roomsGroupsZones.data.length === 0) {
        return rooms;
    }

    for (const item of roomsGroupsZones.data) {
        if (item.value.startsWith(ROOM_TAG_PREFIX)) {
            rooms.push(cloneDeep(item));
        }
    }

    return rooms;
};

export const retrieveReportRoomsFromSensors = async (
    roomSensors: Device[],
    currentOrgId: string,
    allDevices: Device[],
) => {
    const reportRooms: ReportRoom[] = [];

    const rawRooms = await retrieveReportRawRooms(currentOrgId);

    if (rawRooms.length === 0) {
        return reportRooms;
    }

    for (const rawRoom of rawRooms) {
        // ignore empty room because cannot identify the network of the room
        if (!rawRoom.devices || rawRoom.devices.length === 0) {
            continue;
        }

        reportRooms.push(transformRawRoomToReportRoom(roomSensors, rawRoom, allDevices));
    }

    return reportRooms;
};

/**
 * Retrieve report item data of room or room motion for Report page
 * @param roomSensors The LDSU sensors of all rooms
 * @param timeRangeSelected The selected range
 * @returns
 */
const computeReportResults = (
    curSeries: { series: ReportChartSeries[]; categories: string[] },
    prevSeries: { series: ReportChartSeries[]; categories: string[] },
    isCurrentInvalid: boolean,
    isPreviousInvalid: boolean,
    endDate: moment.Moment,
) => {
    const newSummaryInfoOfItem = summaryReportChartSeries(
        isCurrentInvalid,
        isPreviousInvalid,
        curSeries.series,
        prevSeries.series,
    );

    const prevAverage = getAverageFromSeriesAndTotal(
        newSummaryInfoOfItem.all.previousTotal,
        newSummaryInfoOfItem.weekdays.previousTotal,
        newSummaryInfoOfItem.weekends.previousTotal,
        prevSeries.series,
    );

    const overallData = transformConsumptionDataToOverallData(
        newSummaryInfoOfItem.all.total,
        newSummaryInfoOfItem.weekdays.total,
        newSummaryInfoOfItem.weekends.total,
        curSeries.series,
        curSeries.categories,
        endDate,
        prevAverage,
    );

    const individualData = transformConsumptionDataToIndividualData(
        curSeries.series,
        curSeries.categories,
        endDate,
    );

    return { newSummaryInfoOfItem, overallData, individualData };
};

export const retrieveReportRoomData = async (
    rooms: ReportRoom[],
    timeRangeSelected: MonthTimeRange,
) => {
    if (rooms.length === 0) {
        return {
            newSummaryInfoOfItem: {
                all: cloneDeep(initialSummaryInfoSingleType),
                weekdays: cloneDeep(initialSummaryInfoSingleType),
                weekends: cloneDeep(initialSummaryInfoSingleType),
            },
            overallItemsData: cloneDeep(initialReportOverallData),
            individualItemsData: [] as ReportIndividualData[],
            curSeries: { series: [], categories: [] },
            prevSeries: { series: [], categories: [] },
        };
    }

    const endDayOfMonth = moment(timeRangeSelected.endDate).endOf('month');
    const previousEndDayOfMonth = moment(endDayOfMonth).subtract(1, 'month');
    const curCategories = getCategoryOfMonth(endDayOfMonth);
    const prevCategories = getCategoryOfMonth(previousEndDayOfMonth);

    const curUsage = await retrieveUsageDataOfRooms(rooms, endDayOfMonth);
    const prevUsage = await retrieveUsageDataOfRooms(rooms, previousEndDayOfMonth);

    const { series: curSeries, isInvalid: isCurrentInvalid } = createItemSeriesFromRoomUsageData(curUsage, rooms, curCategories, endDayOfMonth);
    const { series: prevSeries, isInvalid: isPreviousInvalid } = createItemSeriesFromRoomUsageData(prevUsage, rooms, prevCategories, previousEndDayOfMonth);

    const curSeriesData = { series: curSeries, categories: curCategories };
    const prevSeriesData = { series: prevSeries, categories: prevCategories };

    const results = computeReportResults(
        curSeriesData, prevSeriesData,
        isCurrentInvalid, isPreviousInvalid,
        timeRangeSelected.endDate,
    );

    return {
        newSummaryInfoOfItem: results.newSummaryInfoOfItem,
        overallItemsData: results.overallData,
        individualItemsData: results.individualData,
        curSeries: curSeriesData,
        prevSeries: prevSeriesData,
    };
};

export const retrieveReportMeterData = async (
    meters: Device[],
    currentOrgId: string,
    endDayOfMonth: moment.Moment,
    timeRangeSelected: MonthTimeRange,
    timeSeries: string,
    metricType: 'waterflow' | 'energy',
) => {
    if (meters.length === 0) {
        return {
            newSummaryInfoOfItem: {
                all: cloneDeep(initialSummaryInfoSingleType),
                weekdays: cloneDeep(initialSummaryInfoSingleType),
                weekends: cloneDeep(initialSummaryInfoSingleType),
            },
            overallMetersData: cloneDeep(initialReportOverallData),
            individualMetersData: [] as ReportIndividualData[],
            curSeries: { series: [], categories: [] },
            prevSeries: { series: [], categories: [] },
        };
    }

    const sensorDetails = meters.map((meter) => {
        return {
            device_id: meter.ldsuUID,
            said: meter.said,
            gateway_id: meter.gatewayId,
            name: meter.sensorName,
            color: '#F2994A',
            organization_id: currentOrgId,
            show_alert: false,
            time_series: timeSeries,
        };
    });

    const groupBy = ConsumptionChartBy.Month;
    const { metricData, previousMetricData } = await reportService.getConsumptionMetricForReportPage(
        sensorDetails,
        endDayOfMonth.unix(),
        groupBy,
        metricType,
    );

    const timeUnit = timeUnitMapping[groupBy];
    const previousTime = moment(endDayOfMonth).subtract(1, timeUnit);
    const curSeries = computeReportChartSeries(sensorDetails, ConsumptionChartBy.Month, metricData, endDayOfMonth);
    const prevSeries = computeReportChartSeries(sensorDetails, ConsumptionChartBy.Month, previousMetricData, previousTime);

    const results = computeReportResults(
        curSeries, prevSeries,
        metricData.length === 0, previousMetricData.length === 0,
        timeRangeSelected.endDate,
    );

    return {
        newSummaryInfoOfItem: results.newSummaryInfoOfItem,
        overallMetersData: results.overallData,
        individualMetersData: results.individualData,
        curSeries,
        prevSeries,
    };
};

export const retrieveReportSensorData = async (
    sensors: Device[],
    type: ReportSensorType | 'waterLeakage',
    timeRangeSelected: MonthTimeRange,
) => {
    const endDayOfMonth = moment(timeRangeSelected.endDate).endOf('month');
    const prevMonth = moment(endDayOfMonth).subtract(1, 'month').endOf('month');
    const curTime = endDayOfMonth.unix();
    const prevTime = prevMonth.unix();
    const curCategories = getCategoryOfMonth(endDayOfMonth);
    const prevCategories = getCategoryOfMonth(prevMonth);
    const curSeries: ReportChartSeries[] = [];
    const prevSeries: ReportChartSeries[] = [];

    for (const sensor of sensors) {
        const id = `${sensor.gatewayId}-${sensor.deviceId}`;

        const rawDeviceData = await reportService.getSensorData(
            sensor.gatewayId,
            sensor.ldsuUID,
            sensor.said,
            curTime,
            type,
        );
        const prevRawDeviceData = await reportService.getSensorData(
            sensor.gatewayId,
            sensor.ldsuUID,
            sensor.said,
            prevTime,
            type,
        );

        const singleCurSeries = createItemSeriesFromRawData(
            id,
            sensor.sensorName,
            rawDeviceData,
            curCategories,
            endDayOfMonth,
            type === 'waterLeakage' ? 'sumPulse' : 'avg',
        );

        const singlePrevSeries = createItemSeriesFromRawData(
            id,
            sensor.sensorName,
            prevRawDeviceData,
            prevCategories,
            prevMonth,
            type === 'waterLeakage' ? 'sumPulse' : 'avg',
        );

        curSeries.push(singleCurSeries);
        prevSeries.push(singlePrevSeries);
    }

    return {
        curSeries: {
            series: curSeries,
            categories: curCategories,
        },
        prevSeries: {
            series: prevSeries,
            categories: prevCategories,
        },
    };
};

export const retrieveReportWaterLeakageData = async (
    sensors: Device[],
    timeRangeSelected: MonthTimeRange,
) => {
    if (sensors.length === 0) {
        return {
            newSummaryInfoOfItem: {
                all: cloneDeep(initialSummaryInfoSingleType),
                weekdays: cloneDeep(initialSummaryInfoSingleType),
                weekends: cloneDeep(initialSummaryInfoSingleType),
            },
            overallMetersData: cloneDeep(initialReportOverallData),
            individualMetersData: [] as ReportIndividualData[],
            curSeries: { series: [], categories: [] },
            prevSeries: { series: [], categories: [] },
        };
    }

    const { curSeries, prevSeries } = await retrieveReportSensorData(
        sensors,
        'waterLeakage',
        timeRangeSelected,
    );

    const results = computeReportResults(
        curSeries, prevSeries,
        !isReportChartSeriesValid(curSeries.series),
        !isReportChartSeriesValid(prevSeries.series),
        timeRangeSelected.endDate,
    );

    return {
        newSummaryInfoOfItem: results.newSummaryInfoOfItem,
        overallMetersData: results.overallData,
        individualMetersData: results.individualData,
        curSeries,
        prevSeries,
    };
};

export const retrieveRoomsAndMeters = async (devices: GatewayDeviceList, currentOrgId: string) => {
    let roomVirtualSensors: Device[] = [];
    let energyMeters: Device[] = [];
    let flowMeters: Device[] = [];

    // prevent crashing when the fetchDevice is not successful.
    if (devices.deviceList?.length > 0) {
        roomVirtualSensors = devices.deviceList.filter((sensor) => sensor.obj === ROOM_VIRTUAL_LDSU_OBJ);

        let energyFilter = getWidgetConfigByWidgetType(WidgetType.METER_CHART_ENERGY_CONSUMPTION).filter?.(WidgetType.METER_CHART_ENERGY_CONSUMPTION);
        energyFilter ??= (sensor: Device) => getSensorType({ CLS: sensor.cls }) === SensorType.EnergyConsumption; // fallback

        energyMeters = devices.deviceList.filter(energyFilter);

        let waterFilter = getWidgetConfigByWidgetType(WidgetType.METER_CHART_WATER_CONSUMPTION).filter?.(WidgetType.METER_CHART_WATER_CONSUMPTION);
        waterFilter ??= (sensor: Device) => getSensorType({ CLS: sensor.cls }) === SensorType.EnergyConsumption; // fallback

        flowMeters = devices.deviceList.filter(waterFilter);

        // sort energy meters by name
        energyMeters.sort((a, b) => {
            return a.sensorName.localeCompare(b.sensorName);
        });
        // sort water flow meters by name
        flowMeters.sort((a, b) => {
            return a.sensorName.localeCompare(b.sensorName);
        });
    }

    const rooms = await retrieveReportRoomsFromSensors(roomVirtualSensors, currentOrgId, devices.deviceList);

    // sort room by name
    rooms.sort((a, b) => {
        return a.roomName.localeCompare(b.roomName);
    });

    return {
        rooms,
        energyMeters,
        flowMeters,
    };
};

export const retrieveSensors = (devices: GatewayDeviceList) => {
    const filterAndSort = (sensorType: string): Device[] => {
        if (!devices.deviceList?.length) return [];
        const filtered = devices.deviceList.filter((sensor) => sensor.sensorType === sensorType);
        filtered.sort((a, b) => a.sensorName.localeCompare(b.sensorName));
        return filtered;
    };

    return {
        waterLeakageSensors: filterAndSort(REPORT_SENSOR_REGISTRY.waterLeakage.deviceSensorType!),
        temperatureSensors: filterAndSort(REPORT_SENSOR_REGISTRY.temperature.deviceSensorType!),
        humiditySensors: filterAndSort(REPORT_SENSOR_REGISTRY.humidity.deviceSensorType!),
        co2Sensors: filterAndSort(REPORT_SENSOR_REGISTRY.co2.deviceSensorType!),
        pressureSensors: filterAndSort(REPORT_SENSOR_REGISTRY.pressure.deviceSensorType!),
        tvocSensors: filterAndSort(REPORT_SENSOR_REGISTRY.tvoc.deviceSensorType!),
        pm1Sensors: filterAndSort(REPORT_SENSOR_REGISTRY.pm1.deviceSensorType!),
        pm25Sensors: filterAndSort(REPORT_SENSOR_REGISTRY["pm2.5"].deviceSensorType!),
        pm10Sensors: filterAndSort(REPORT_SENSOR_REGISTRY.pm10.deviceSensorType!),
    };
};

export const retrieveReportPageData = async (
    items: ReportItemsState,
    timeRangeSelected: MonthTimeRange,
    currentOrgId: string,
    endDayOfMonth: moment.Moment,
    isApplied: boolean,
    setState: {
        setItemsLoading: ReportSetItemLoadingFunction,
        setItemsData: React.Dispatch<React.SetStateAction<ReportItemStateData>>,
        setSummaryInfo: React.Dispatch<React.SetStateAction<ReportSummaryInfo>>,
        setOverviewData: React.Dispatch<React.SetStateAction<ReportOverviewData>>,
    },
) => {
    const usedIds = isApplied ? 'appliedIds' : 'selectedIds';

    const setSensorData = (type: ReportItemType, data: {
        curSeries: {
            series: ReportChartSeries[];
            categories: string[];
        },
        prevSeries: {
            series: ReportChartSeries[];
            categories: string[];
        },
    }) => {
        setState.setItemsData((prev) => {
            return {
                ...prev,
                [type]: {
                    chartSeries: data.curSeries.series,
                    prevChartSeries: data.prevSeries.series,
                },
            }
        });
    };

    const processItemData = async <T extends {
        newSummaryInfoOfItem: ReportSummaryInfoOfOneItem;
        curSeries: { series: ReportChartSeries[]; categories: string[] };
        prevSeries: { series: ReportChartSeries[]; categories: string[] };
    }>(
        type: ReportItemType,
        retrieveFn: () => Promise<T>,
        getOverview: (result: T) => { overallData: ReportOverallData; individualData: ReportIndividualData[] },
        errorMsg: string,
    ) => {
        try {
            const result = await retrieveFn();
            setSensorData(type, result);
            setState.setOverviewData((prev) => ({ ...prev, [type]: getOverview(result) }));
            setState.setSummaryInfo((prev) => ({
                all: { ...prev.all, [type]: { ...result.newSummaryInfoOfItem.all } },
                weekdays: { ...prev.weekdays, [type]: { ...result.newSummaryInfoOfItem.weekdays } },
                weekends: { ...prev.weekends, [type]: { ...result.newSummaryInfoOfItem.weekends } },
            }));
        } catch (error) {
            console.error(errorMsg, error);
        } finally {
            setState.setItemsLoading(type, false);
        }
    };

    const metersOverview = <T extends { overallMetersData: ReportOverallData; individualMetersData: ReportIndividualData[] }>(r: T) => ({
        overallData: r.overallMetersData,
        individualData: r.individualMetersData,
    });

    await Promise.allSettled([
        processItemData(
            'room',
            () => retrieveReportRoomData(
                convertSelectedIdsToRooms(items.room.all, items.room[usedIds]),
                timeRangeSelected,
            ),
            (r) => ({ overallData: r.overallItemsData, individualData: r.individualItemsData }),
            'Failed to retrieve room data in Report',
        ),
        processItemData(
            'energy',
            () => retrieveReportMeterData(
                convertSelectedIdsToDevices(items.energy.all, items.energy[usedIds]),
                currentOrgId, endDayOfMonth, timeRangeSelected,
                'mqtt2tsdb_activepower_kw', 'energy',
            ),
            metersOverview,
            'Failed to retrieve energy meters data in Report',
        ),
        processItemData(
            'water',
            () => retrieveReportMeterData(
                convertSelectedIdsToDevices(items.water.all, items.water[usedIds]),
                currentOrgId, endDayOfMonth, timeRangeSelected,
                'mqtt2tsdb_flowrate_m3ph', 'waterflow',
            ),
            metersOverview,
            'Failed to retrieve water flow meters data in Report',
        ),
        processItemData(
            'waterLeakage',
            () => retrieveReportWaterLeakageData(
                convertSelectedIdsToDevices(items.waterLeakage.all, items.waterLeakage[usedIds]),
                timeRangeSelected,
            ),
            metersOverview,
            'Failed to retrieve water leakage detection data in Report',
        ),

        ...REPORT_SENSOR_LIST.map((sensorType) => new Promise((resolve) => {
            const type = sensorType;

            if (items[type].all.length === 0) {
                setState.setItemsLoading(type, false);
                resolve('OK');
                return;
            }

            retrieveReportSensorData(
                convertSelectedIdsToDevices(items[type].all, items[type][usedIds]),
                type,
                timeRangeSelected,
            ).then((sensorData) => {
                setSensorData(type, sensorData);
            }).catch((error) => {
                console.error(`Failed to retrieve ${type} sensor data in Report`, error);
            }).finally(() => {
                setState.setItemsLoading(type, false);
                resolve('OK');
            });
        })),
    ]);
};
